import { useId, type ReactNode } from "react";

import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from "@mui/material";

export interface ConfirmDialogProps {
  open: boolean;
  title: string;
  message: ReactNode;
  /**
   * Callout rendered under the message, for the consequence of the secondary
   * action (what leaving costs, how to come back). Omitted when there is none.
   */
  warning?: ReactNode;
  /** Tone of the `warning` callout: a cost to accept, or guidance to note. */
  warningSeverity?: "info" | "warning";
  /** Label for the emphasised (filled) action. */
  primaryLabel: string;
  onPrimary: () => void;
  /** Label for the secondary (outlined) action. */
  secondaryLabel: string;
  onSecondary: () => void;
  /**
   * Confine the dialog and its backdrop to this element instead of covering the
   * viewport, so the rest of the page stays visible and interactive. The element
   * must establish a positioning context (e.g. `position: relative`).
   */
  container?: HTMLElement | null;
  /**
   * `compact` (default) is the narrow, tight layout. `comfortable` is wider
   * with more space between title, message, callout and actions, for prompts
   * that carry a longer message or a `warning` callout.
   */
  size?: "compact" | "comfortable";
}

const LAYOUT = {
  compact: {
    maxWidth: "xs",
    titleSx: { pb: 1 },
    contentSx: { pb: 1 },
    warningSx: { mt: 2 },
    actionsSx: { px: 3, pb: 3, pt: 2 },
  },
  comfortable: {
    maxWidth: "sm",
    titleSx: { fontSize: "1.375rem", pb: 1.5, pt: 4, px: 4 },
    contentSx: { pb: 1, px: 4 },
    warningSx: { mt: 3 },
    actionsSx: { px: 4, pb: 4, pt: 3 },
  },
} as const;

/**
 * Two-choice prompt. Deliberately not dismissible — neither Escape nor a
 * backdrop click closes it, because callers use it where one of the two answers
 * must be recorded (leaving an editor, discarding changes, sharing a record).
 *
 * The two actions are named by emphasis, not by outcome: primary is the filled
 * button and secondary the outlined one. Which of them is the "safe" or
 * "proceed" answer is the caller's business — for a leave-without-saving
 * prompt the emphasised choice is usually the one that stays put. Primary is
 * focused on open and sits on the trailing edge, so the safe answer is the one
 * a hurried tap lands on.
 */
export const ConfirmDialog = ({
  open,
  title,
  message,
  warning,
  warningSeverity = "warning",
  primaryLabel,
  onPrimary,
  secondaryLabel,
  onSecondary,
  container,
  size = "compact",
}: ConfirmDialogProps) => {
  const layout = LAYOUT[size];
  const baseId = useId();
  const titleId = `${baseId}-title`;
  const messageId = `${baseId}-message`;
  const warningId = `${baseId}-warning`;

  return (
    <Dialog
      open={open}
      container={container}
      disablePortal={Boolean(container)}
      fullWidth
      maxWidth={layout.maxWidth}
      aria-labelledby={titleId}
      aria-describedby={warning ? `${messageId} ${warningId}` : messageId}
      sx={
        container
          ? {
              position: "absolute",
              "& .MuiBackdrop-root": { position: "absolute" },
              "& .MuiDialog-container": { position: "absolute" },
            }
          : undefined
      }
    >
      <DialogTitle
        id={titleId}
        sx={{ color: "text.primary", fontWeight: 700, ...layout.titleSx }}
      >
        {title}
      </DialogTitle>
      <DialogContent sx={layout.contentSx}>
        <DialogContentText id={messageId} sx={{ color: "text.primary" }}>
          {message}
        </DialogContentText>
        {warning && (
          <Alert
            id={warningId}
            severity={warningSeverity}
            variant="outlined"
            sx={layout.warningSx}
          >
            {warning}
          </Alert>
        )}
      </DialogContent>
      <DialogActions
        disableSpacing
        sx={{
          alignItems: "stretch",
          // Stacked on phones, primary on top; side by side from sm up, primary
          // on the trailing edge.
          flexDirection: { xs: "column", sm: "row-reverse" },
          gap: 2,
          justifyContent: { xs: "flex-start", sm: "space-between" },
          ...layout.actionsSx,
        }}
      >
        <Button
          variant="contained"
          onClick={onPrimary}
          sx={{ width: { xs: "100%", sm: "auto" } }}
          // The dialog cannot be dismissed, so focus has to start on one of the
          // two actions; the emphasised one is the safe answer.
          // eslint-disable-next-line jsx-a11y/no-autofocus
          autoFocus
        >
          {primaryLabel}
        </Button>
        <Button
          variant="outlined"
          onClick={onSecondary}
          sx={{ width: { xs: "100%", sm: "auto" } }}
        >
          {secondaryLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
